import React, { useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DeleteIcon from "@material-ui/icons/Delete";
import { toast } from "react-toastify";

import VariableInserter from "./VariableInserter";
import { extractVarIndices } from "./templatePayload";
import {
  getAcceptForFormat,
  validateFile,
  useTemplateMediaUpload,
} from "./useTemplateMediaUpload";
import { MAX_HEADER_LENGTH } from "./templateValidation";

const useStyles = makeStyles((theme) => ({
  section: {
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  formats: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  toolbar: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  uploadBox: {
    border: "2px dashed",
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textAlign: "center",
    cursor: "pointer",
  },
  mediaPreview: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
  },
  thumb: {
    width: 80,
    height: 80,
    objectFit: "cover",
    borderRadius: 4,
    background: "#f0f0f0",
  },
  charCount: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textAlign: "right",
  },
}));

const FORMAT_OPTIONS = [
  { value: "TEXT", label: "Texto" },
  { value: "IMAGE", label: "Imagem" },
  { value: "VIDEO", label: "Vídeo" },
  { value: "DOCUMENT", label: "Documento" },
];

const HeaderSection = ({ form, setForm, whatsappId, fieldError }) => {
  const classes = useStyles();
  const textRef = useRef(null);
  const fileInputRef = useRef(null);
  const [localError, setLocalError] = useState(null);
  const { upload, uploading } = useTemplateMediaUpload(whatsappId);

  const setEnabled = (enabled) =>
    setForm((prev) => ({ ...prev, header: { ...prev.header, enabled } }));

  const setFormat = (format) =>
    setForm((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        format,
        text: format === "TEXT" ? prev.header.text : "",
        media:
          format === "TEXT"
            ? { localUrl: "", mediaMetaId: "", headerHandle: "", fileName: "" }
            : prev.header.media,
      },
    }));

  const setText = (text) =>
    setForm((prev) => ({ ...prev, header: { ...prev.header, text } }));

  const setExample = (idx, value) =>
    setForm((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        examples: { ...prev.header.examples, [idx]: value },
      },
    }));

  const handleFile = async (file) => {
    if (!file) return;
    const err = validateFile(file, form.header.format);
    if (err) {
      setLocalError(err);
      toast.error(err);
      return;
    }
    setLocalError(null);
    try {
      const data = await upload(file);
      if (data) {
        setForm((prev) => ({
          ...prev,
          header: {
            ...prev.header,
            media: {
              localUrl: data.localUrl || "",
              mediaMetaId: data.mediaMetaId || "",
              headerHandle: data.headerHandle || "",
              fileName: data.fileName || file.name,
            },
          },
        }));
        toast.success("Mídia enviada.");
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err.message;
      setLocalError(msg);
      toast.error(msg);
    }
  };

  const removeMedia = () =>
    setForm((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        media: { localUrl: "", mediaMetaId: "", headerHandle: "", fileName: "" },
      },
    }));

  const indices = extractVarIndices(form.header.text || "");

  return (
    <Box className={classes.section}>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!form.header.enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            color="primary"
          />
        }
        label={<Typography className={classes.sectionTitle}>Cabeçalho (opcional)</Typography>}
      />

      {form.header.enabled && (
        <>
          <RadioGroup
            row
            value={form.header.format}
            onChange={(e) => setFormat(e.target.value)}
            className={classes.formats}
          >
            {FORMAT_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio color="primary" size="small" />}
                label={opt.label}
              />
            ))}
          </RadioGroup>

          {form.header.format === "TEXT" ? (
            <>
              <TextField
                inputRef={textRef}
                value={form.header.text || ""}
                onChange={(e) => setText(e.target.value.replace(/\n/g, ""))}
                fullWidth
                variant="outlined"
                size="small"
                label="Texto do cabeçalho (até 1 variável {{1}})"
                inputProps={{ maxLength: MAX_HEADER_LENGTH }}
                error={!!fieldError}
                helperText={fieldError}
              />
              <Typography className={classes.charCount}>
                {form.header.text?.length || 0}/{MAX_HEADER_LENGTH}
              </Typography>
              <Box className={classes.toolbar}>
                <VariableInserter
                  inputRef={textRef}
                  value={form.header.text}
                  onChange={setText}
                  max={1}
                />
              </Box>
              {indices.map((idx) => (
                <TextField
                  key={`hex-${idx}`}
                  label={`Exemplo para {{${idx}}}`}
                  value={form.header.examples?.[idx] || ""}
                  onChange={(e) => setExample(idx, e.target.value)}
                  fullWidth
                  size="small"
                  variant="outlined"
                  margin="dense"
                  required
                />
              ))}
            </>
          ) : (
            <>
              {!form.header.media?.headerHandle &&
                !form.header.media?.localUrl &&
                !form.header.media?.mediaMetaId && (
                <Box
                  className={classes.uploadBox}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <>
                      <CloudUploadIcon style={{ fontSize: 36, color: "#999" }} />
                      <Typography variant="body2">
                        Clique para enviar {form.header.format.toLowerCase()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {form.header.format === "IMAGE" && "JPG/PNG até 5MB"}
                        {form.header.format === "VIDEO" && "MP4/3GP até 16MB"}
                        {form.header.format === "DOCUMENT" && "PDF/DOC/XLS/PPT até 100MB"}
                      </Typography>
                    </>
                  )}
                </Box>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept={getAcceptForFormat(form.header.format)}
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  handleFile(file);
                }}
              />

              {(form.header.media?.headerHandle ||
                form.header.media?.localUrl ||
                form.header.media?.mediaMetaId) && (
                <Box className={classes.mediaPreview}>
                  {form.header.format === "IMAGE" && form.header.media?.localUrl ? (
                    <img
                      className={classes.thumb}
                      src={form.header.media.localUrl}
                      alt="header"
                    />
                  ) : (
                    <Box className={classes.thumb} />
                  )}
                  <Box flex={1}>
                    <Typography variant="body2">
                      {form.header.media?.fileName || "Mídia carregada"}
                    </Typography>
                    {form.header.media?.headerHandle && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        Handle Meta: {form.header.media.headerHandle.substring(0, 28)}…
                      </Typography>
                    )}
                    {form.header.media?.mediaMetaId && (
                      <Typography variant="caption" color="textSecondary">
                        Meta ID: {form.header.media.mediaMetaId}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Trocar
                  </Button>
                  <IconButton size="small" onClick={removeMedia}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {localError && (
                <Typography color="error" variant="caption">
                  {localError}
                </Typography>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default HeaderSection;
